import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'
import { updateVisitAsync, updatePetSurvey } from '../actions/visits/visits.actions'
import { postDaycareAddOnsAsync } from '../actions/addons/addons.actions'
import useSelectedFacility from './useSelectedFacility'
import moment from 'moment'
import { CUTOFFS } from '../utils'

const DAYCARE_HOLIDAY_TYPES = ['total', 'daycare', 'daytime']

const useDaycareVisit = () => {
	const dispatch = useDispatch()
	const loading = useSelector((state) => state.specific.visits.loading)
	const { facility } = useSelectedFacility()

	const [updating, setUpdating] = useState(false)

	const daycare = useMemo(() => {
		if (!facility || !facility.services?.daycare) {
			return null
		}
		const {
			services: { daycare: daycareServices }
		} = facility

		return daycareServices
	}, [facility])

	const prices = useMemo(() => {
		if (!daycare) {
			return null
		}

		return daycare?.prices || null
	}, [daycare])

	const getCancellationPolicy = useCallback(
		(numPets) => {
			if (!daycare) {
				return 'Error getting cancellation policy'
			}

			if (daycare.generic_cancellation_policy) return daycare.generic_cancellation_policy

			if (!daycare.charge_cancellation_fee) {
				return `${daycare.no_fee_text}`
			}

			const end_time = moment(daycare.free_period_end_time, 'HH:mm').format('h:mm a')
			const end_date = CUTOFFS[daycare.free_period_end_date] || ''
			const mid_time = moment(daycare.mid_period_end_time, 'HH:mm').format('h:mm a')
			const mid_date = CUTOFFS[daycare.mid_period_end_date] || ''

			const extra_dogs = numPets - 1

			const fee_one = (
				(daycare.mid_period_base_fee + (daycare.mid_period_extra_dog_fee || 0) * extra_dogs) /
				100
			).toFixed(2)

			const full_fee_one_dog = `$${(daycare.final_period_base_fee / 100).toFixed(2)}`
			const full_fee_extra_dog = `$${(daycare.final_period_extra_dog_fee / 100).toFixed(2)}`

			return `If you need to cancel for any reason, you will not be charged if you cancel before ${end_time} ${end_date} the appointment. Cancellations made after ${end_time} ${end_date} the appointment but before ${mid_time} ${mid_date} the appointment will result in an $${fee_one} fee. Cancellations made after ${mid_time} ${mid_date} the appointment or no-shows will result in a fee of ${full_fee_one_dog} for the first dog plus ${full_fee_extra_dog} for each additional dog on the appointment.`
		},
		[daycare]
	)
	/**
	 * Daycare holidays for the current facility from 1 month past and forward
	 */
	const daycareHolidays = useMemo(() => {
		if (!facility) {
			return []
		}
		const { holidays } = facility || []

		const filtered = holidays.filter(({ type, startDate }) => {
			return (
				DAYCARE_HOLIDAY_TYPES.includes(type) &&
				moment(startDate).isSameOrAfter(moment().startOf('month').subtract(1, 'month'))
			)
		})

		return filtered
	}, [facility])

	const isADaycareHoliday = useCallback(
		({ date }) => {
			if (!facility) {
				return false
			}

			const daycareHoliday = daycareHolidays.find((holiday) => {
				const { startDate, endDate } = holiday

				const start = moment(startDate).startOf('day')
				const end = moment(endDate).endOf('day')
				const day = moment(date)

				const dayIsBewteen = day.isBetween(start, end)
				return dayIsBewteen
			})

			return daycareHoliday || null
		},
		[daycareHolidays]
	)

	const update = useCallback(
		async ({ visitId, updates = {} }) => {
			const body = {
				visit_id: visitId,
				updates
			}
			setUpdating(true)
			const res = await updateVisitAsync(dispatch, body)
			setUpdating(false)
			return res
		},
		[dispatch]
	)

	const savePetSurvey = useCallback(
		async (survey) => {
			const body = {
				survey
			}
			setUpdating(true)
			const res = await updatePetSurvey(dispatch, body)
			setUpdating(false)
			return res
		},
		[dispatch]
	)

	const updateAddOns = useCallback(
		async ({ visitId, addons = [], petIds = [] }) => {
			if (!visitId) {
				dispatch({ type: 'UPDATE_VISIT_FAIL' })
				return
			}

			const addOnsForPayload = {}

			for (const petId of petIds) {
				addOnsForPayload[petId] = []
			}

			addons.forEach((addon) => {
				const {
					_id: rawId,
					addon_id: existingAddOnId,
					category,
					type,
					name,
					addon_choice_id,
					pet_id,
					time,
					date,
					training_service = false
				} = addon

				const record = {
					category,
					pet_id,
					date,
					training_service
				}

				if (existingAddOnId || rawId) {
					record['addon_id'] = existingAddOnId || rawId
				}

				if (name) {
					record['name'] = name
				}

				if (time) {
					record['time'] = time
				}

				if (type) {
					record['type'] = type
				}

				if (addon_choice_id) {
					record['addon_choice_id'] = addon_choice_id
				}

				if (addOnsForPayload[pet_id]) {
					addOnsForPayload[pet_id].push(record)
				}
			})

			setUpdating(true)
			await postDaycareAddOnsAsync(dispatch, visitId, addOnsForPayload)
			setUpdating(false)
		},
		[dispatch]
	)

	return {
		loading,
		update,
		updateAddOns,
		facility,
		getCancellationPolicy,
		prices,
		daycare,
		updating,
		isADaycareHoliday,
		daycareHolidays,
		savePetSurvey
	}
}

export default useDaycareVisit
